import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import SEO from "../components/SEO";
import CTAButton from "../components/CTAButton";
import { Reveal, Lines } from "../components/Reveal";
import { FinalCTA } from "./Home";
import { getProject } from "../lib/api";

const Meta = ({ label, value }) => (
  <div className="py-4 border-t border-ink/10">
    <p className="label-tech text-graphite mb-1.5">{label}</p>
    <p className="text-[15px] text-coal">{value}</p>
  </div>
);

const ProjectDetail = () => {
  const { slug } = useParams();
  const [project, setProject] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    setProject(null);
    setError(false);
    getProject(slug).then(setProject).catch(() => setError(true));
  }, [slug]);

  if (error) {
    return (
      <div className="pt-52 pb-40 container-ax" data-testid="project-not-found">
        <h1 className="t-h2 text-coal mb-8">Project not found.</h1>
        <CTAButton to="/projects" variant="dark" testId="back-to-projects">All Projects</CTAButton>
      </div>
    );
  }

  if (!project) {
    return <div className="pt-52 pb-40 container-ax text-graphite" data-testid="project-loading">Loading project…</div>;
  }

  return (
    <>
      <SEO title={project.seo_title} description={project.seo_description} />
      <section className="pt-36 sm:pt-44 bg-paper" data-testid="project-detail">
        <div className="container-ax">
          <Reveal>
            <Link to="/projects" data-testid="breadcrumb-projects" className="label-tech text-graphite hover:text-cobalt transition-colors inline-flex items-center gap-2 mb-10">
              <ArrowLeft size={14} /> Projects / {project.type}
            </Link>
          </Reveal>
          <h1 className="t-h1 text-coal max-w-5xl" data-testid="project-title">
            <Lines lines={[project.title]} animateOnMount />
          </h1>
          <Reveal i={2} className="mt-8 flex flex-wrap gap-x-10 gap-y-2 label-tech text-graphite">
            <span>{project.location}, {project.country}</span>
            <span>{project.year}</span>
            <span className="text-cobalt">{project.status}</span>
            {project.placeholder && <span className="text-graphite/50 normal-case tracking-normal">(Concept study — placeholder imagery)</span>}
          </Reveal>
        </div>
        <div className="container-ax mt-14">
          <Reveal className="overflow-hidden">
            <img src={project.lead_image} alt={`${project.title} — lead image (placeholder)`} className="w-full aspect-[16/8] object-cover" data-testid="project-hero-image" />
          </Reveal>
        </div>
      </section>

      <section className="section-pad bg-paper">
        <div className="container-ax grid grid-cols-12 gap-10">
          <div className="col-span-12 lg:col-span-3">
            <Meta label="Client" value={project.client} />
            <Meta label="Type" value={project.type} />
            <Meta label="Floor area" value={project.floor_area} />
            <Meta label="Scope of services" value={project.scope.join(", ")} />
          </div>
          <div className="col-span-12 lg:col-span-7 lg:col-start-5">
            <Reveal>
              <p className="label-tech text-graphite mb-6">Overview</p>
              <p className="t-intro text-coal">{project.description}</p>
            </Reveal>
            <div className="grid sm:grid-cols-2 gap-10 mt-16">
              <Reveal i={1}>
                <p className="label-tech text-graphite mb-4">Challenge</p>
                <p className="text-[15px] text-graphite leading-relaxed">{project.challenge}</p>
              </Reveal>
              <Reveal i={2}>
                <p className="label-tech text-graphite mb-4">Response</p>
                <p className="text-[15px] text-graphite leading-relaxed">{project.response}</p>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-paper pb-24 sm:pb-32" data-testid="project-gallery">
        <div className="container-ax grid grid-cols-12 gap-6">
          {project.gallery.map((g, i) => {
            const wide = i % 3 === 0;
            return (
              <Reveal key={g} i={i % 3} className={`col-span-12 ${wide ? "" : i % 3 === 1 ? "md:col-span-7" : "md:col-span-5 md:mt-16"} overflow-hidden`}>
                <img src={g} alt={`${project.title} — gallery image ${i + 1} (placeholder)`} loading="lazy" className={`w-full object-cover hover:scale-[1.03] transition-transform duration-700 ease-expo ${wide ? "aspect-[16/8]" : "aspect-[4/3]"}`} />
              </Reveal>
            );
          })}
        </div>
      </section>

      <section className="bg-paper pb-24 sm:pb-32">
        <div className="container-ax grid grid-cols-12 gap-10">
          <div className="col-span-12 md:col-span-5">
            <Reveal>
              <p className="label-tech text-graphite mb-6">Materials</p>
              <ul>
                {project.materials.map((m) => (
                  <li key={m} className="py-3.5 border-t border-ink/10 text-[15px] text-coal">{m}</li>
                ))}
              </ul>
            </Reveal>
          </div>
          <div className="col-span-12 md:col-span-5 md:col-start-8">
            <Reveal i={1}>
              <p className="label-tech text-graphite mb-6">Credits</p>
              <ul>
                {project.credits.map((c) => (
                  <li key={c} className="py-3.5 border-t border-ink/10 text-[15px] text-coal">{c}</li>
                ))}
              </ul>
            </Reveal>
          </div>
        </div>
      </section>

      <nav className="border-t border-ink/10 bg-paper" aria-label="Project navigation" data-testid="project-prev-next">
        <div className="grid grid-cols-2">
          <Link to={`/projects/${project.prev.slug}`} data-testid="prev-project" className="group p-8 sm:p-12 border-r border-ink/10 hover:bg-coal hover:text-paper transition-colors duration-500">
            <span className="label-tech text-graphite group-hover:text-paper/50 flex items-center gap-2 mb-3"><ArrowLeft size={14} /> Previous</span>
            <span className="t-h3">{project.prev.title}</span>
          </Link>
          <Link to={`/projects/${project.next.slug}`} data-testid="next-project" className="group p-8 sm:p-12 text-right hover:bg-coal hover:text-paper transition-colors duration-500">
            <span className="label-tech text-graphite group-hover:text-paper/50 flex items-center justify-end gap-2 mb-3">Next <ArrowRight size={14} /></span>
            <span className="t-h3">{project.next.title}</span>
          </Link>
        </div>
      </nav>
      <FinalCTA />
    </>
  );
};

export default ProjectDetail;
